import { debounce } from 'lodash-es'
import { Button, type ButtonProps } from 'ant-design-vue'
import { CloseIconClass, CloseOutlined } from './closeIcon'
import { useEmptyModal } from './empty'
import Resource from '~/components/resource'

const WarnIcon = transformImage(
  getFeUrl('/youyan-platform/png/delete_warn.png')
)
const SuccessIcon = transformImage(getFeUrl('/youyan-platform/png/success.png'))

interface ConfirmOptions {
  description?: string | VNode // 提示框描述
  icon?: boolean | string // 是否显示图标
  tips?: string // 提示信息
  title?: string | VNode // 提示框标题
  closable?: boolean
  cancel?: boolean
  cancelText?: string // 取消按钮文字
  okText?: string // 确定按钮文字
  okButtonProps?: ButtonProps // 确定按钮属性
  cancelButtonProps?: ButtonProps // 取消按钮属性
  onCancel?: () => Promise<void> | void // 取消按钮点击事件的返回值
  onOk?: () => Promise<void> | void // 确定按钮点击事件的返回值
}

const ConfirmContent = defineComponent({
  name: 'ConfirmContent',
  props: {
    options: {
      type: Object as PropType<ConfirmOptions>,
      default: () => ({}),
    },
    cancel: {
      type: Function as PropType<() => void>,
      default: () => {},
    },
  },
  setup({ options, cancel }) {
    const { $t } = useNuxtApp()
    const loading = ref(false)

    const onCancel = () => {
      options?.onCancel?.()
      cancel()
    }

    const onOk = async () => {
      loading.value = true
      try {
        await options?.onOk?.()
      } finally {
        loading.value = false
      }
      cancel()
    }
    const cancelButtonProps: ButtonProps = {
      type: 'primary',
      ghost: true,
      ...options?.cancelButtonProps,
    }
    const okButtonProps: ButtonProps = {
      type: 'primary',
      ...options?.okButtonProps,
    }
    return () => (
      <div class="relative flex flex-col items-center main-bg p-[30px] rounded-[8px]">
        {options?.closable && (
          <CloseOutlined class={CloseIconClass} onClick={onCancel} />
        )}
        {options?.icon ? (
          <Resource
            src={options?.icon === 'success' ? SuccessIcon : WarnIcon}
            class="w-[48px] h-[48px]"
          />
        ) : null}
        {options?.title ? (
          <span class="text-16 title font-semibold mt-[12px] mb-[10px]">
            {options.title}
          </span>
        ) : null}
        {options?.description ? (
          <span class="text-14 description text-center m-[0_40px]">
            {options.description}
          </span>
        ) : null}
        {options?.tips ? (
          <span class="bg-warning-6 bg-opacity-20 rounded-[8px] p-[10px_12px] flex tips items-start text-12 text-font-60 mt-[16px]">
            Tips: {options.tips}
          </span>
        ) : null}
        <div class="mt-[20px] flex-center gap-x-[20px]">
          {options?.cancel !== false ? (
            <Button
              onClick={onCancel}
              class="min-w-[90px]"
              {...cancelButtonProps}
            >
              {options.cancelText || $t('qu-xiao')}
            </Button>
          ) : null}
          <Button
            onClick={onOk}
            class="min-w-[90px]"
            {...okButtonProps}
            loading={loading.value}
          >
            {options.okText || $t('que-ren')}
          </Button>
        </div>
      </div>
    )
  },
})

/**
 * 使用确认提示框
 * @param options - ConfirmOptions配置项
 */
export const useConfirm = debounce((options: ConfirmOptions) => {
  if (options.closable === undefined) {
    Object.assign(options, { closable: true })
  }

  const cancel = () => {
    modal.destroy()
  }
  const modal = useEmptyModal({
    width: 420,
    content: <ConfirmContent options={options} cancel={cancel} />,
  })
  return modal
}, 200)
